function TermsOfUseLucasAI() {
  return (
    <div>
      <h1>Lucas AI - Terms of Use</h1>

      <p>
        <strong>Last Updated:</strong> Oct 14, 2025
      </p>
      <p>
        <strong>Version:</strong> 5
      </p>

      <p>
        This Terms of Use agreement (this "Agreement") is a legally binding contract between the individual or entity agreeing to
        these terms ("User," "you," or "your") and Lucas Health Corporation, a Delaware corporation, doing business as Lucas AI
        ("Lucas AI," "we," "us," or "our"). This Agreement governs your access to and use of the Lucas AI platform, including any
        related software, services, and websites (collectively, the "Service").
      </p>

      <p>
        By creating an account, clicking "I accept," or by accessing or using the Service in any manner, you affirm that you have
        read, fully understand, and agree to be legally bound by this entire Agreement. If you are entering into this Agreement on
        behalf of a company or other legal entity, you represent and warrant that you have the full legal authority to bind such
        entity to this Agreement.
      </p>

      <div className="warning">
        THIS AGREEMENT CONTAINS A MANDATORY ARBITRATION PROVISION, A WAIVER OF CLASS ACTION RIGHTS, AND COMPREHENSIVE
        INDEMNIFICATION OBLIGATIONS. IT PLACES SOLE AND ULTIMATE RESPONSIBILITY ON YOU, THE USER, FOR THE ACCURACY OF ALL MEDICAL
        DOCUMENTATION AND FOR ANY RESULTING FINANCIAL OR REGULATORY CONSEQUENCES. THE SERVICE IS AN ASSISTIVE TOOL AND IS PROVIDED
        WITHOUT ANY GUARANTEE OF ACCURACY. PLEASE READ THIS DOCUMENT CAREFULLY IN ITS ENTIRETY.
      </div>

      <div className="section">
        <h2>1. The Service: Purpose and Core Limitations</h2>

        <h3>1.1. Purpose</h3>
        <p>
          The Service is an ambient artificial intelligence documentation support tool designed exclusively to assist qualified
          healthcare providers ("Providers") by capturing and helping to document patient-provider interactions.
        </p>

        <h3>1.2. Intended Use for Clinical Documentation Integrity and Assistive Framework</h3>
        <p>
          You acknowledge that the Service is designed to function as an advanced clinical documentation integrity tool. Its core
          purpose is to assist you in creating clinical notes that are a complete, accurate, and consistent reflection of the
          patient encounter and your corresponding medical judgment.
        </p>

        <p>
          We recognize the inherent limitations of ambient documentation, where a provider's full clinical thought process may not
          always be completely verbalized. To help bridge this gap, the Service may present contextual prompts or reminders. You
          understand and agree that these prompts are not treatment directives or recommendations. They are assistive tools
          designed to remind you of common documentation possibilities or clinical data points that align with the standard of
          care, helping you ensure the final note is a comprehensive reflection of your assessment and plan.
        </p>

        <p>
          The Service is designed to be agnostic to any specific treatment, commercial product, proprietary methodology, or
          clinical service. Its logic is based on recognized clinical documentation guidelines and best practices, not on
          promoting or favoring any particular clinical pathway or commercial interest. You retain sole responsibility for all
          clinical judgments and agree to use the Service consistent with this intended use.
        </p>

        <h3>1.3. Core Limitations</h3>
        <p>You acknowledge and agree that:</p>
        <ul>
          <li>
            (a) The Service is a tool designed to assist with documentation, not to create final, verified, or signed medical
            records.
          </li>
          <li>(b) The output of the Service is a draft and is not a substitute for your professional medical judgment.</li>
          <li>(c) Results may vary based on a multitude of situations...</li>
          <li>
            (d) All ultimate responsibility for clinical decisions, documentation accuracy, coding, billing, and regulatory
            compliance remains exclusively with you.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>2. Provider Responsibilities, Warranties, and Patient Consent</h2>

        <h3>2.1. User Categories</h3>
        <p>
          This Terms of Use agreement (this "Agreement") is a legally binding contract between the individual or legal entity
          agreeing to these terms and{" "}
          <strong>Lucas Health Corporation, a Delaware corporation, doing business as Lucas AI</strong> ("Lucas AI," "we," "us,"
          or "our"). If you are an individual subscribing for your own use, you are the "User" ("you," or "your"). If you
          represent a company or other legal entity, that entity is the "User." This Agreement governs the User's access to and
          use of the Lucas AI platform, including any related software, services, and websites (collectively, the "Service").
        </p>

        <p>
          By creating an account, clicking "I accept," or by accessing or using the Service in any manner, you affirm that you
          have read, fully understand, and agree to be legally bound by this entire Agreement. If you are entering into this
          Agreement on behalf of a company or other legal entity (an "Organization"), you represent and warrant that you have the
          full legal authority to bind such entity and its personnel to this Agreement, and this Agreement shall govern all access
          and use of the Service by such Organization and its authorized personnel.
        </p>

        <p>Access to the Service is granted to two categories of individuals:</p>
        <ul>
          <li>
            (a) <strong>Licensed Healthcare Professionals (LHPs):</strong> Individuals who are licensed healthcare professionals
            (e.g., physicians, PAs, NPs) in good standing with all applicable licensing boards.
          </li>
          <li>
            (b) <strong>Authorized Personnel:</strong> Clinical, administrative, and operational staff (including but not limited
            to nurses, medical assistants, scribes, coders, billers, and administrators) who are employed by or contracted with a
            subscribing healthcare organization ("Organization") and have been granted access by that Organization.
          </li>
        </ul>

        <h3>2.2. Role-Based Access and Principle of Minimum Necessary</h3>
        <p>
          You acknowledge that your level of access to the Service is determined by the specific role and permissions granted to
          you by your Organization, governed by the principle of "minimum necessary" access required to perform your designated
          job functions.
        </p>

        <h3>2.3. Representation and Warranty of Patient Consent</h3>
        <p>
          As a material condition of this Agreement, you represent and warrant to Lucas AI that you have provided all necessary
          notices and have obtained all legally required and valid consents from every individual (including patients) whose
          conversations will be captured by the Service before initiating a recording. You acknowledge this is your sole and
          non-delegable duty.
        </p>

        <h3>2.4. Compliance with Recording Laws</h3>
        <p>
          You are solely responsible for complying with all applicable federal, state, and local laws regarding the recording of
          communications. You agree that, as between you and Lucas AI, you are the data controller of any Protected Health
          Information (PHI) captured by the Service.
        </p>

        <h3>2.5. Liability and Indemnification for Consent</h3>
        <p>
          You assume all liability for and agree to indemnify Lucas AI from any and all Claims (as defined in Section 8 of this
          Agreement) arising from your failure to obtain legally sufficient patient consent.
        </p>

        <h3>2.6. Universal Professional Responsibility</h3>
        <p>
          Regardless of your role, you agree to use the Service solely for legitimate, authorized purposes, maintain the
          confidentiality of all information, and comply with all applicable professional and ethical standards.
        </p>

        <h3>2.7. Right to Suspend</h3>
        <p>
          Lucas AI reserves the right to suspend or terminate any account found to be in breach of the obligations set forth in
          this section.
        </p>
      </div>

      <div className="section">
        <h2>3. Data Processing, Analytics, and Retention</h2>

        <h3>3.1. Data Ownership and Processing</h3>
        <p>
          As between you and Lucas AI, you retain all right, title, and interest in and to the Protected Health Information (PHI)
          you submit to the Service. You authorize us to process all data you submit to the Service, including audio files
          containing PHI, for the purpose of providing the Service to you, as further described in our Business Associate
          Agreement (BAA).
        </p>

        <h3>3.2. Service Improvement and Analytics</h3>
        <p>
          To help us improve the quality, accuracy, and functionality of the Service, you grant Lucas AI the right to use data
          processed through the Service for internal quality control, sentiment analysis, and to train and improve our underlying
          software and artificial intelligence models. For any such purpose, we will only use data that has been de-identified in
          accordance with the standards set forth in the HIPAA Privacy Rule (45 C.F.R. § 164.514(b)). Lucas AI is the exclusive
          owner of all such De-Identified Data.
        </p>

        <h3>3.3. Audio Recording and Transcript Retention Policy</h3>
        <p>
          You acknowledge and agree to our standard data retention policy: audio recordings and the raw text transcriptions
          generated from them are temporarily stored in a secure and HIPAA-compliant manner only as long as necessary to generate
          the clinical note summary and perform related quality control checks. Immediately following the completion of this
          process, the original audio recordings and raw transcriptions are permanently and irrecoverably deleted from our
          systems. The final, generated text summaries remain as part of the medical documentation accessible to you.
        </p>

        <p>
          <strong>Exception for Contracted Research Studies:</strong> Our standard deletion policy shall apply in all cases unless
          modified by a separate, written "Research Agreement" between Lucas AI and the legal entity that employs or is affiliated
          with you (your "Organization"). As a precondition to any such Research Agreement and any subsequent retention of audio
          data, the Organization must provide Lucas AI with verifiable documentation of legally sufficient informed patient
          consent for the specific research purpose, which must, where required by law, include approval from a duly constituted
          Institutional Review Board (IRB). Lucas AI will only retain audio recordings and/or raw transcriptions beyond the
          standard deletion period upon its verified receipt of such documentation and only in accordance with the terms of the
          governing Research Agreement. If you are a User participating in a study covered by such an agreement, you acknowledge
          this modified retention policy and agree to strictly comply with all of your Organization's research protocols and
          consent procedures.
        </p>
      </div>

      <div className="section">
        <h2>4. Account and User Security</h2>
        <p>
          You must provide accurate and current information upon registration. You are solely and entirely responsible for all
          activity that occurs under your account. You must implement and maintain reasonable administrative, physical, and
          technical safeguards to protect your account credentials. You must notify Lucas AI immediately in writing of any
          unauthorized use or security breach related to your account. Lucas AI is not liable for any loss or damage arising from
          your failure to comply with these security obligations.
        </p>
      </div>

      <div className="section">
        <h2>5. Intellectual Property and Service Ownership</h2>

        <h3>5.1. Ownership</h3>
        <p>
          You acknowledge and agree that the Service, including its underlying software, source code, architecture, algorithms,
          and all associated intellectual property rights, is the sole and exclusive property of Lucas AI. The Service is licensed
          to you on a limited, revocable, non-exclusive, non-sublicensable, and non-transferable basis; it is not sold.
        </p>

        <h3>5.2. Restrictions</h3>
        <p>You are expressly prohibited from:</p>
        <ul>
          <li>(a) creating derivative works from the Service;</li>
          <li>(b) attempting to access the source code or other non-public materials;</li>
          <li>
            (c) sublicensing, selling, renting, or otherwise commercializing the Service without express written permission;
          </li>
          <li>(d) copying, decompiling, modifying, or reverse-engineering the Service or its software.</li>
        </ul>

        <h3>5.3. Trademarks</h3>
        <p>The marks LUCAS AI and associated logos are our exclusive trademarks.</p>
      </div>

      <div className="section">
        <h2>6. Prohibited Conduct and Intended Use</h2>
        <p>
          Your use of the Service must be legal, ethical, and strictly for its intended purpose. You are strictly forbidden from
          using the Service to:
        </p>
        <ul>
          <li>(a) Create false, misleading, fraudulent, or artificially generated medical documentation;</li>
          <li>
            (b) Fabricate compliance documentation for services not authentically rendered or for patient encounters that did not
            occur;
          </li>
          <li>(c) Generate documentation to artificially create or "reverse-engineer" medical compliance records;</li>
          <li>(d) Facilitate or justify the overutilization or misuse of any medical treatments or pharmaceuticals;</li>
          <li>
            (e) Engage in any activity that constitutes, facilitates, or could be construed as Medicare fraud, Medicaid fraud,
            insurance fraud, healthcare billing abuse, or a violation of any applicable local, state, or federal law, including
            but not limited to the Anti-Kickback Statute, the Physician Self-Referral Law (Stark Law), or the False Claims Act;
          </li>
          <li>
            (f) Use any automated process (such as a robot or scraper) to access the Service for any purpose other than its
            intended use; or introduce any viruses, Trojan horses, worms, or other material that is malicious or technologically
            harmful; or attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the Service, the
            server on which the Service is stored, or any server, computer, or database connected to the Service.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>7. Subscription, Fees, and Cancellation</h2>

        <h3>7.1. Subscription, Automatic Payments, and Billing</h3>
        <p>
          To access the Service, you must provide and maintain a valid, authorized payment method on file, such as a credit card
          (your "Payment Method"). An active subscription requires an active Payment Method with authorization for automatic
          payments. The Service is offered on a subscription basis and is billed in advance on a recurring, periodic basis (a
          "Billing Cycle"). Your Billing Cycle will be specified at the time of your purchase. By providing your Payment Method
          and agreeing to these Terms, you expressly authorize Lucas AI to automatically charge the applicable subscription fees
          to your Payment Method for each recurring Billing Cycle until your subscription is cancelled by you in accordance with
          Section 7.2. You are responsible for ensuring your Payment Method information remains current, valid, and accurate at
          all times.
        </p>

        <p>
          If your Payment Method is declined or if a charge is otherwise unsuccessful, we will notify you and may attempt to
          process the payment again. We reserve the right to suspend your access to the Service without liability until the
          outstanding payment is received in full. This suspension may prevent you from accessing any and all data, including
          patient documentation, stored on the Service. We reserve the right to terminate your account for prolonged or repeated
          non-payment. For the purposes of this section, "prolonged or repeated non-payment" is defined as any instance where fees
          remain unpaid for more than sixty (60) days from the due date or after three (3) consecutive failed payment attempts.
        </p>

        <h3>7.2. User Responsibility for Cancellation</h3>
        <p>
          You have the sole responsibility to manage and cancel your subscription. To avoid being charged for the next Billing
          Cycle, you must cancel your subscription prior to the commencement of that cycle. You may cancel your subscription at
          any time through one of the following methods:
        </p>
        <ul>
          <li>
            (a) Accessing the profile or account settings section within the Service application and following the cancellation
            procedures;
          </li>
          <li>(b) Sending a written cancellation request to accounting@lucashealth.ai.</li>
        </ul>
        <p>
          You acknowledge that subscription fees entitle you to access the Service and are not based on your actual usage.
          Therefore, a failure to use the Service or a lack of activity during a paid Billing Cycle does not relieve you of your
          payment obligations and does not constitute a valid reason for a refund. Upon successful cancellation, your subscription
          will not renew, and your access to the Service will continue through the end of your current paid Billing Cycle.
        </p>

        <h3>7.3. Refund Policy</h3>
        <p>
          Except when required by law, all fees are non-refundable. Any refunds, credits, or adjustments that may be offered are
          provided on a strictly case-by-case basis and at the sole and absolute discretion of Lucas Health Corporation. A
          decision to provide a refund in one instance does not create an obligation or establish a precedent for Lucas Health
          Corporation to provide any refund in any other instance.
        </p>

        <h3>7.4. Price Changes</h3>
        <p>
          Except as otherwise specified in a separate, executed agreement between you and Lucas AI that provides for a fixed term
          and price, we reserve the right to change the subscription fees for the Service. We will provide you with at least
          thirty (30) days' prior notice of any fee changes. Price changes will become effective at the start of the next Billing
          Cycle following the notice period. Your continued use of the Service after the price change constitutes your agreement
          to pay the modified fee amount.
        </p>

        <h3>7.5. Taxes</h3>
        <p>
          All fees are exclusive of any and all applicable federal, state, and local taxes, duties, or tariffs (collectively,
          "Taxes"). You are solely responsible for paying all Taxes associated with your subscription to the Service.
        </p>

        <h3>7.6. Free Trials and Promotions</h3>
        <p>
          Lucas AI may, at its sole discretion, offer a subscription with a free trial for a limited period of time. If you
          register for a free trial, you will be required to provide your Payment Method. We will begin billing your Payment
          Method for the regular subscription fees on the first day following the end of the free trial period, on a recurring
          basis, unless you cancel your subscription prior to the end of the trial period in accordance with Section 7.2.
        </p>
      </div>

      <div className="section">
        <h2>8. Indemnification (Your Comprehensive Obligation to Protect Us)</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Lucas AI, its authorized resellers, affiliates, officers, directors,
          employees, and agents (collectively, the "Indemnified Parties") from and against any and all third-party claims,
          demands, suits, actions, proceedings, government investigations, audits, judgments, losses, damages, liabilities, fines,
          penalties, and expenses of any kind (including reasonable attorneys' fees and all legal costs), and any demands for
          recoupment or clawback from any payer (collectively, "Claims") arising out of or in any way related to:
        </p>
        <ul>
          <li>(a) Your material breach of this Agreement or violation of any applicable law;</li>
          <li>(b) Your clinical malpractice, professional negligence, or violation of any professional standard of care;</li>
          <li>
            (c) Your documentation, billing, or coding practices, including but not limited to Claims arising from alleged
            fraudulent documentation or intentional billing fraud;
          </li>
          <li>
            (d) Any Claim whatsoever resulting from the inaccuracy, incompleteness, or alleged insufficiency of any documentation
            generated by the Service that you failed to independently and thoroughly verify, edit, and correct.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>9. WARRANTY EXCLUSIONS AND LIMITATION OF LIABILITY</h2>

        <h3>9.1. WARRANTY DISCLAIMER</h3>
        <div className="warning">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITH ALL FAULTS AND WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM
          EXTENT PERMITTED BY LAW, LUCAS AI EXPRESSLY DISCLAIMS ANY AND ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY,
          INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE
          DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE OR THAT INFORMATION OBTAINED FROM IT WILL BE ACCURATE OR RELIABLE.
          THERE IS NO GUARANTEE OR PROMISE AS TO THE ACCURACY, COMPLETENESS, OR SUITABILITY OF ANY OUTPUT FOR ANY PURPOSE,
          INCLUDING CLINICAL DOCUMENTATION OR BILLING.
        </div>

        <h3>9.2. EXCLUSION OF DAMAGES</h3>
        <div className="warning">
          UNDER NO CIRCUMSTANCES WILL LUCAS AI OR ITS AFFILIATES AND RESELLERS BE LIABLE TO YOU FOR ANY INCIDENTAL, CONSEQUENTIAL,
          INDIRECT, SPECIAL, PUNITIVE, OR EXEMPLARY DAMAGES.
        </div>

        <h3>9.3. MAXIMUM AGGREGATE LIABILITY</h3>
        <div className="warning">
          IN NO EVENT SHALL THE TOTAL AGGREGATE LIABILITY OF LUCAS AI AND ITS AFFILIATES AND RESELLERS EXCEED THE GREATER OF (A)
          THE TOTAL FEES PAID BY YOU FOR THE SERVICE DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S.
          DOLLARS ($100.00).
        </div>
      </div>

      <div className="section">
        <h2>10. User's Sole Responsibility for Verification, Accuracy, and All Outcomes</h2>

        <h3>10.1. Acknowledgment of AI Limitations</h3>
        <p>
          You understand and acknowledge that the Service uses artificial intelligence, which is an evolving technology. The
          output generated by the Service is an automated draft and may contain factual inaccuracies, errors, omissions, or
          misinterpretations. It is not a finished medical or legal document and should never be treated as such.
        </p>

        <h3>10.2. Absolute and Non-Delegable Duty to Verify</h3>
        <p>
          You have an absolute, primary, and non-delegable duty to independently review, edit, and verify every part of the output
          generated by the Service before signing, approving, or using it for any purpose, including but not limited to, entry
          into a medical record, clinical decision-making, or submission for billing or reimbursement. You must correct any and
          all inaccuracies to ensure the final documentation is a true and accurate reflection of the patient encounter.
        </p>

        <h3>10.3. Sole Liability for All Consequences</h3>
        <p>
          You are solely and exclusively liable for all content within your final clinical documentation and for any and all
          outcomes resulting from that documentation. Lucas AI and its resellers shall bear no liability or responsibility
          whatsoever for any adverse consequences arising from your use of the Service's output, including but not limited to:
        </p>
        <ul>
          <li>(a) inappropriate clinical decision-making;</li>
          <li>(b) any misrepresentation or fraudulent activity;</li>
          <li>(c) denied claims, payer audits, recoupments, or clawbacks;</li>
          <li>(d) government or regulatory investigations or penalties;</li>
          <li>(e) any action that could trigger an investigation by any regulatory authority.</li>
        </ul>
      </div>

      <div className="section">
        <h2>11. Termination</h2>

        <h3>11.1 Termination by Us</h3>
        <p>
          We may suspend or terminate your account and this Agreement immediately and without notice for any material breach of
          these terms, including but not limited to non-payment of fees or a breach of Section 6 (Prohibited Conduct).
        </p>

        <h3>11.2. Effect of Termination</h3>
        <p>
          Upon termination, your right to access and use the Service will immediately cease. You remain liable for all fees and
          charges incurred up to the date of termination. Following termination, Lucas AI may, subject to our obligations under
          the BAA and applicable law, permanently delete all of your data from our systems.
        </p>

        <h3>11.3. Survival</h3>
        <p>The following sections shall survive any termination: 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, and 13.</p>
      </div>

      <div className="section">
        <h2>12. Governing Law and Dispute Resolution</h2>

        <h3>12.1. Governing Law</h3>
        <p>This Agreement is governed by the laws of the State of Florida.</p>

        <h3>12.2. Mandatory Arbitration</h3>
        <p>
          All disputes arising out of this Agreement shall be resolved exclusively by final and binding arbitration in Miami-Dade
          County, Florida.
        </p>

        <h3>12.3. Class Action Waiver</h3>
        <div className="warning">
          YOU AND LUCAS AI AGREE TO BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS
          MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
        </div>
      </div>

      <div className="section">
        <h2>13. Miscellaneous Provisions</h2>

        <h3>13.1. Entire Agreement</h3>
        <p>
          This Agreement, along with our Privacy Policy and Business Associate Agreement (BAA), is the entire agreement between
          you and Lucas AI regarding the Service. The BAA is hereby incorporated by reference into this Agreement. In the event of
          any conflict between the terms of this Agreement and the terms of the BAA with respect to the handling of Protected
          Health Information (PHI), the terms of the BAA shall control.
        </p>

        <h3>13.2. Severability</h3>
        <p>
          If any provision is found to be unenforceable, it will be limited or eliminated to the minimum extent necessary so that
          this Agreement will otherwise remain in full force.
        </p>

        <h3>13.3. Contact Information</h3>
        <p>For legal notices, contact us at: legal@lucashealth.ai</p>

        <h3>13.4. Notices</h3>
        <p>
          Any notices or other communications provided by Lucas AI under this Agreement, including those regarding modifications
          to this Agreement, will be given: (i) via email to the address associated with your account; or (ii) by posting to the
          Service. For notices made by email, the date of receipt will be deemed the date on which such notice is transmitted.
        </p>

        <h3>13.5. Force Majeure</h3>
        <p>
          Neither party shall be liable for any failure or delay in performance under this Agreement (other than for delay in the
          payment of money due and payable) for causes beyond that party's reasonable control and occurring without that party's
          fault or negligence, including, but not limited to, acts of God, acts of government, flood, fire, civil unrest, acts of
          terror, strikes or other labor problems, or computer networking or telecommunications failures.
        </p>

        <h3>13.6. Assignment</h3>
        <p>
          You may not assign or transfer this Agreement, by operation of law or otherwise, without our prior written consent. Any
          attempt by you to assign or transfer this Agreement, without such consent, will be null and of no effect. Lucas AI may
          freely assign or transfer this Agreement without restriction. Subject to the foregoing, this Agreement will bind and
          inure to the benefit of the parties, their successors, and permitted assigns.
        </p>

        <h3>13.7. Feedback</h3>
        <p>
          If you provide us with any ideas, suggestions, or proposals concerning the Service ("Feedback"), you grant us a
          perpetual, irrevocable, worldwide, royalty-free, and fully paid-up right to use, disclose, reproduce, modify, and
          otherwise exploit such Feedback for any purpose without any obligation or compensation to you.
        </p>
      </div>
    </div>
  );
}

export default TermsOfUseLucasAI;
